import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';

// "User Guide" call-to-action dropped at the bottom of each tool's main page, linking to
// that tool's guide route. Shared so every tool page gets the same treatment.
export const ToolGuideCta = ({ guidePath, toolName }) => (
  <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-xl border border-structure bg-aluminum p-6">
    <div className="flex items-center gap-3">
      <BookOpen className="h-5 w-5 text-autoxium-blue shrink-0" />
      <p className="text-sm text-muted-foreground">
        Want the full input/output reference, formulas, and a worked example for the {toolName}?
      </p>
    </div>
    <Link to={guidePath} data-testid="tool-guide-link" className="shrink-0">
      <Button className="btn-premium">
        <BookOpen className="h-4 w-4" />
        Read the User Guide
      </Button>
    </Link>
  </div>
);

// Shared shell for every tool's User Guide page: dark hero (with a "Back to Tool" link),
// document version line, and a content column. Only the section content passed as
// children differs between guides — this is the one place the guide "look" is defined.
export const GuideLayout = ({ toolName, toolPath, docVersion = '1.0', docDate, children }) => (
  <div className="min-h-screen" data-testid="guide-page">
    <section className="bg-chassis text-white py-16 md:py-24">
      <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
        <div className="max-w-3xl">
          <Link
            to={toolPath}
            className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors text-sm mb-6"
            data-testid="guide-back-to-tool-link-top"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Tool
          </Link>
          <p className="font-mono text-xs tracking-widest text-autoxium-blue uppercase mb-4 flex items-center gap-2">
            <BookOpen className="h-3.5 w-3.5" /> User Guide
          </p>
          <h1 className="font-chivo text-4xl md:text-6xl font-black uppercase tracking-tighter mb-6">
            {toolName}
          </h1>
          <p className="font-mono text-xs text-white/50">
            Document Version {docVersion}
            {docDate ? ` · ${docDate}` : ''}
          </p>
        </div>
      </div>
    </section>

    <section className="py-12 md:py-16">
      <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
        <div className="max-w-4xl space-y-8">
          {children}
          <div className="pt-6 border-t border-structure">
            <Link
              to={toolPath}
              className="inline-flex items-center gap-2 text-autoxium-blue font-mono text-sm uppercase tracking-wider hover:underline"
              data-testid="guide-back-to-tool-link-bottom"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to {toolName}
            </Link>
          </div>
        </div>
      </div>
    </section>
  </div>
);

// Top-level numbered section (1, 2, 3, ...), rendered as a technical-card so guides read
// consistently with the rest of the site's card-based layout.
export const GuideSection = ({ number, title, children }) => (
  <Card className="technical-card" id={`section-${number}`} data-testid={`guide-section-${number}`}>
    <CardHeader>
      <CardTitle className="font-chivo text-xl md:text-2xl font-bold uppercase tracking-tight">
        {number}. {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-6 text-sm leading-relaxed text-foreground/90">
      {children}
    </CardContent>
  </Card>
);

// Numbered sub-section (1.1, 5.2, ...) inside a GuideSection.
export const GuideSubSection = ({ number, title, children }) => (
  <div className="space-y-3">
    <h3 className="font-chivo text-sm font-bold uppercase tracking-wider text-autoxium-blue">
      {number} {title}
    </h3>
    <div className="space-y-3 text-sm text-foreground/90 leading-relaxed">{children}</div>
  </div>
);

// Consistent table styling for Input/Output Parameter tables, Worked Example tables, and
// the Validation table. `rows` is an array of arrays of cell content (string or node).
export const ParamTable = ({ columns, rows }) => (
  <div className="rounded-md border border-structure overflow-hidden">
    <Table>
      <TableHeader>
        <TableRow className="bg-muted/50 hover:bg-muted/50">
          {columns.map((c, j) => (
            <TableHead key={j} className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground whitespace-nowrap">
              {c}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={i}>
            {row.map((cell, j) => (
              <TableCell key={j} className="text-xs align-top py-3">{cell}</TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

// Line-per-step block used for "Step-by-Step Calculation" (formula with substituted
// numbers, one step per line). Body font (steps are full sentences, not code) with
// tabular digits; break-words so values never split mid-number.
export const StepList = ({ steps }) => (
  <ol className="space-y-2">
    {steps.map((s, i) => (
      <li key={i} className="text-sm leading-relaxed tabular-nums bg-muted/50 rounded-md px-4 py-2.5 break-words">
        {s}
      </li>
    ))}
  </ol>
);

export const BulletList = ({ items }) => (
  <ul className="space-y-2 list-disc pl-5">
    {items.map((it, i) => (
      <li key={i} className="text-sm leading-relaxed">{it}</li>
    ))}
  </ul>
);

export default GuideLayout;
